import { useQuery } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';

/**
 * React Query hook to fetch draft job post data
 *
 * Fetches draft data from the posting service API and transforms it
 * to a format suitable for the job details form.
 *
 * @param draftId - The unique identifier for the draft job post
 * @returns React Query result object with draft data and query states
 *
 * @example
 * ```tsx
 * function JobPostingPage() {
 *   const { id } = useParams();
 *   const { data, isLoading, error } = useDraftJobPostData(id);
 *
 *   if (isLoading) return <Spinner centered variant="default" size="md" />;
 *   if (error) return <ErrorShower />;
 *
 *   return <JobDetailsForm initialValues={data} />;
 * }
 * ```
 */
export function useGetDraftJobPost(draftId?: string) {
  const query = useQuery({
    queryKey: ['draftJobPost', draftId],
    queryFn: () => PermanentJobsService.getDraftJobPost(draftId!),
    enabled: !!draftId,
    refetchOnWindowFocus: false,
  });

  const { data, isLoading, isFetching } = query;

  return {
    ...query,
    data: data?.data,
    isInitialLoading: isLoading,
    isLoading: isLoading || isFetching,
  };
}
